// The draft addresses (docs/superpowers/specs/2026-10-13-multi-ticket-drafts-design.md,
// "Addresses"). Pure, for node --test (test/web/draft-address.test.js).
//
// `/support/tickets/new` is never a screen of its own: it mints a GUID and REPLACES itself with
// `/support/tickets/drafts/<guid>`. Its query string (`?parent=106558` from a ticket's (+), a
// customer) becomes a one-time prefill in router state, not part of the draft's address,
// so reloading the draft never applies it again — the bug where reloading
// `/support/tickets/new?parent=X` overwrote the restored draft with a fresh copy of the parent.

import { newDraftId } from './draft-model.js';
import { supportPath } from '../../support-path.js';

/** The query keys a way in may carry. Anything else is not a prefill and is dropped. */
export const PREFILL_KEYS = ['parent', 'customer'];

/** Where a draft lives; tab-model.js builds the same href for its tab. */
export function draftHref(id) {
  return supportPath(`/tickets/drafts/${id}`);
}

/**
 * The prefill a query string asks for: `{ parent?, customer? }` with trimmed, non-empty
 * values (the first of a repeated key, as `useSearchParams().get` reads it), or null when
 * there is nothing to prefill.
 *
 * @param {string|URLSearchParams|null|undefined} search  `location.search`, with or without `?`.
 */
export function prefillFromSearch(search) {
  const params = search instanceof URLSearchParams ? search : new URLSearchParams(search ?? '');
  const prefill = {};
  for (const key of PREFILL_KEYS) {
    const value = params.get(key)?.trim();
    if (value) prefill[key] = value;
  }
  return Object.keys(prefill).length ? prefill : null;
}

/**
 * Where `/tickets/new` goes: a freshly minted draft, with the prefill in router state.
 * Navigate there with `{ replace: true, state }` so Back skips `/tickets/new`.
 *
 * @param {string|URLSearchParams} search
 * @param {() => string} [mintId]  Injected for tests; newDraftId otherwise.
 * @returns {{ to: string, state: { prefill: object|null } }}
 */
export function newDraftLocation(search, mintId = newDraftId) {
  return { to: draftHref(mintId()), state: { prefill: prefillFromSearch(search) } };
}
